import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Users,
  Settings,
  Brain,
  Target,
  TrendingUp,
  Briefcase,
  Heart,
  Building2
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const NewTriaxPage = () => {
  const [activeAxis, setActiveAxis] = useState(null);
  const { t } = useLanguage();

  // Axes (Struktur / Kultur / Mensch) – Texte depuis i18n, Icons & Colors en dur
  const rawAxes = t('triaxPage.axes');
  const axesMeta = [
    {
      id: 'struktur',
      icon: Settings,
      color: 'from-purple-600 to-blue-600'
    },
    {
      id: 'kultur',
      icon: Users,
      color: 'from-orange-500 to-red-500'
    },
    {
      id: 'mensch',
      icon: Brain,
      color: 'from-emerald-500 to-teal-600'
    }
  ];
  const axes = Array.isArray(rawAxes)
    ? axesMeta.map((meta) => {
        const item =
          rawAxes.find((a) => a.id === meta.id) || rawAxes[0] || {};
        return {
          ...meta,
          ...item
        };
      })
    : [];

  // Interactions (Kultur → Mensch, etc.)
  const rawInteractions = t('triaxPage.interactions');
  const interactionsMeta = [
    {
      id: 'culture-to-human',
      icon: Users,
      color: 'from-orange-500 to-red-500'
    },
    {
      id: 'human-to-structure',
      icon: Building2,
      color: 'from-emerald-500 to-teal-600'
    },
    {
      id: 'structure-to-culture',
      icon: Target,
      color: 'from-purple-600 to-blue-600'
    }
  ];
  const interactions = Array.isArray(rawInteractions)
    ? interactionsMeta.map((meta, index) => {
        const item = rawInteractions[index] || rawInteractions[0] || {};
        return {
          ...meta,
          ...item
        };
      })
    : [];

  // Professionen
  const rawProfessionals = t('triaxPage.professionals');
  const professionalsMeta = [
    {
      id: 'change-manager',
      icon: Briefcase,
      color: 'from-purple-600 to-blue-600'
    },
    {
      id: 'coaches',
      icon: Heart,
      color: 'from-emerald-500 to-teal-600'
    },
    {
      id: 'triax-consultants',
      icon: Target,
      color: 'from-orange-500 to-red-500'
    }
  ];
  const professionals = Array.isArray(rawProfessionals)
    ? professionalsMeta.map((meta) => {
        const item =
          rawProfessionals.find((p) => p.id === meta.id) ||
          rawProfessionals[0] ||
          {};
        return {
          ...meta,
          ...item
        };
      })
    : [];

  // Key Benefits
  const rawBenefits = t('triaxPage.benefits');
  const benefitsMeta = [
    {
      id: 'systemic',
      icon: Target,
      color: 'from-purple-600 to-blue-600'
    },
    {
      id: 'scientific',
      icon: TrendingUp,
      color: 'from-orange-500 to-red-500'
    },
    {
      id: 'practical',
      icon: Users,
      color: 'from-emerald-500 to-teal-600'
    },
    {
      id: 'sustainable',
      icon: ArrowRight,
      color: 'from-blue-500 to-cyan-500'
    }
  ];
  const benefits = Array.isArray(rawBenefits)
    ? benefitsMeta.map((meta) => {
        const item =
          rawBenefits.find((b) => b.id === meta.id) || rawBenefits[0] || {};
        return {
          ...meta,
          ...item
        };
      })
    : [];

  const handleAxisClick = (axisId) => {
    setActiveAxis(activeAxis === axisId ? null : axisId);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-24 pt-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -left-20 w-96 h-96 bg-gradient-to-r from-purple-600/20 to-orange-500/20 rounded-full blur-3xl animate-float" />
          <div
            className="absolute bottom-1/4 -right-20 w-80 h-80 bg-gradient-to-r from-orange-500/20 to-purple-600/20 rounded-full blur-3xl animate-float"
            style={{ animationDelay: '-3s' }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-6xl font-light text-white mb-6">
            {t('triaxPage.hero.titleBeforeHighlight')}{' '}
            <span className="gradient-text font-medium">
              {t('triaxPage.hero.highlight')}
            </span>
            {t('triaxPage.hero.suffix')}
          </h1>

          <p className="text-xl text-slate-300 max-w-4xl mx-auto leading-relaxed font-light mb-8">
            {t('triaxPage.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Why TRIAX Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            {/* Classical Approach */}
            <div className="p-8 bg-slate-50 rounded-2xl border border-slate-200">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                {t('triaxPage.classical.title')}
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {t('triaxPage.classical.text')}
              </p>
              <div className="space-y-3">
                {Array.isArray(t('triaxPage.classical.bullets')) &&
                  t('triaxPage.classical.bullets').map((bullet, idx) => (
                    <div key={idx} className="flex items-start">
                      <div className="w-2 h-2 bg-slate-400 rounded-full mt-2 mr-3" />
                      <p className="text-slate-600">
                        <strong>{bullet.bold}</strong> {bullet.text}
                      </p>
                    </div>
                  ))}
                <p className="text-slate-500 italic mt-4">
                  {t('triaxPage.classical.footer')}
                </p>
              </div>
            </div>

            {/* TRIAX Approach */}
            <div className="p-8 bg-gradient-to-br from-purple-50 to-orange-50 rounded-2xl border-2 border-purple-200">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4 flex items-center">
                <Target className="w-6 h-6 text-purple-600 mr-2" />
                {t('triaxPage.triaxApproach.title')}
              </h3>
              <p className="text-slate-700 mb-6 leading-relaxed">
                {t('triaxPage.triaxApproach.text')}
              </p>
              <div className="p-4 bg-white/70 rounded-xl">
                <p className="text-slate-700 font-medium">
                  {t('triaxPage.triaxApproach.highlight')}
                </p>
              </div>
            </div>
          </div>

          {/* Your Advantage */}
          <div className="bg-gradient-to-r from-purple-50 via-white to-orange-50 rounded-2xl p-8 border border-purple-100">
            <h3 className="text-2xl font-semibold text-slate-900 mb-6 text-center">
              {t('triaxPage.advantage.title')}
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {Array.isArray(t('triaxPage.advantage.items')) &&
                t('triaxPage.advantage.items').map((item, idx) => {
                  const Icon =
                    idx === 0
                      ? Target
                      : idx === 1
                      ? TrendingUp
                      : Users;
                  const gradient =
                    idx === 0
                      ? 'from-purple-600 to-blue-600'
                      : idx === 1
                      ? 'from-orange-500 to-red-500'
                      : 'from-emerald-500 to-teal-600';
                  return (
                    <div key={idx} className="text-center">
                      <div
                        className={`w-12 h-12 bg-gradient-to-r ${gradient} rounded-xl mx-auto mb-3 flex items-center justify-center`}
                      >
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="font-semibold text-slate-900 mb-2">
                        {item.title}
                      </h4>
                      <p className="text-slate-600 text-sm">
                        {item.description}
                      </p>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </section>

      {/* How TRIAX Works - Interactive Diagram */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-5xl font-light text-slate-900 mb-4">
              {t('triaxPage.how.titleBeforeHighlight')}{' '}
              <span className="gradient-text font-medium">
                {t('triaxPage.how.highlight')}
              </span>
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              {t('triaxPage.how.subtitle')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Interactive Venn Diagram */}
            <div className="relative w-full h-[500px]">
              {axes.map((axis) => {
                const AxisIcon = axis.icon;
                // Positions by id
                let style = {};
                if (axis.id === 'struktur') {
                  style = {
                    top: '5%',
                    left: '50%',
                    transform: 'translateX(-50%)'
                  };
                } else if (axis.id === 'kultur') {
                  style = {
                    bottom: '5%',
                    left: '10%'
                  };
                } else if (axis.id === 'mensch') {
                  style = {
                    bottom: '5%',
                    right: '10%'
                  };
                }
                return (
                  <div
                    key={axis.id}
                    className="absolute w-64 h-64 rounded-full cursor-pointer transition-all duration-300 hover:scale-105"
                    style={{
                      ...style,
                      background:
                        axis.id === 'struktur'
                          ? 'linear-gradient(135deg, #9333ea 0%, #3b82f6 100%)'
                          : axis.id === 'kultur'
                          ? 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)'
                          : 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
                      opacity: activeAxis === axis.id ? 0.95 : 0.7
                    }}
                    onClick={() => handleAxisClick(axis.id)}
                  >
                    <div className="flex flex-col items-center justify-center h-full text-white">
                      <AxisIcon className="w-12 h-12 mb-2" />
                      <div className="text-sm font-medium">{axis.label}</div>
                      <div className="text-lg font-bold">{axis.vennTitle}</div>
                    </div>
                  </div>
                );
              })}

              {/* Center Circle - TRIAX® */}
              <div
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                style={{ zIndex: 10 }}
              >
                <div
                  className="w-24 h-24 rounded-full flex items-center justify-center shadow-2xl"
                  style={{
                    background:
                      'radial-gradient(circle, #f97316 0%, #9333ea 50%, #10b981 100%)'
                  }}
                >
                  <span className="text-white font-bold text-sm">
                    TRIAX®
                  </span>
                </div>
              </div>
            </div>

            {/* Axis Details Card */}
            <div>
              {activeAxis ? (
                <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg">
                  {(() => {
                    const axis = axes.find((a) => a.id === activeAxis);
                    if (!axis) return null;
                    const AxisIcon = axis.icon;
                    return (
                      <>
                        <div className="flex items-center mb-6">
                          <div
                            className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${axis.color} flex items-center justify-center mr-4 shadow-lg`}
                          >
                            <AxisIcon className="w-8 h-8 text-white" />
                          </div>
                          <div>
                            <div className="text-sm text-slate-500">
                              {axis.label}
                            </div>
                            <h3 className="text-2xl font-semibold text-slate-900">
                              {axis.title}
                            </h3>
                            <p className="text-slate-600">
                              {axis.subtitle}
                            </p>
                          </div>
                        </div>

                        <p className="text-slate-700 leading-relaxed mb-6">
                          {axis.description}
                        </p>

                        <h4 className="text-lg font-semibold text-slate-900 mb-4">
                          {t('triaxPage.axesSection.examplesTitle')}
                        </h4>
                        <ul className="space-y-3">
                          {Array.isArray(axis.examples) &&
                            axis.examples.map((example, idx) => (
                              <li key={idx} className="flex items-start">
                                <div
                                  className={`w-2 h-2 rounded-full bg-gradient-to-r ${axis.color} mt-2 mr-3 flex-shrink-0`}
                                />
                                <span className="text-slate-600">
                                  {example}
                                </span>
                              </li>
                            ))}
                        </ul>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center p-12 bg-white rounded-2xl border border-slate-200 shadow-lg">
                  <Target className="w-16 h-16 text-purple-600 mx-auto mb-6" />
                  <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                    {t('triaxPage.how.inactiveTitle')}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {t('triaxPage.how.inactiveSubtitle')}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="mt-12 text-center p-6 bg-gradient-to-r from-purple-50 to-orange-50 rounded-2xl border border-purple-100">
            <p className="text-slate-700 font-medium">
              {t('triaxPage.how.footnote')}
            </p>
          </div>
        </div>
      </section>

      {/* Systemic Effects - How Each Axis Influences Others */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-light text-slate-900 mb-4">
              <span className="gradient-text font-medium">
                {t('triaxPage.interactionsSection.title')}
              </span>
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              {t('triaxPage.interactionsSection.subtitle')}
            </p>
          </div>

          <div className="space-y-8">
            {interactions.map((interaction, idx) => (
              <div
                key={idx}
                className="bg-gradient-to-r from-slate-50 to-white rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-start gap-6">
                  <div
                    className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${interaction.color} flex items-center justify-center flex-shrink-0 shadow-lg`}
                  >
                    <interaction.icon className="w-8 h-8 text-white" />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-2xl font-semibold text-slate-900">
                        {interaction.title}
                      </h3>
                      <span className="text-sm text-slate-500 font-medium">
                        ({interaction.from} → {interaction.to})
                      </span>
                    </div>

                    <p className="text-slate-700 mb-4 leading-relaxed">
                      {interaction.description}
                    </p>

                    <div className="grid md:grid-cols-3 gap-4 mb-4">
                      {Array.isArray(interaction.examples) &&
                        interaction.examples.map((example, i) => (
                          <div key={i} className="flex items-start">
                            <div
                              className={`w-2 h-2 rounded-full bg-gradient-to-r ${interaction.color} mt-2 mr-2 flex-shrink-0`}
                            />
                            <p className="text-slate-600 text-sm">
                              {example}
                            </p>
                          </div>
                        ))}
                    </div>

                    <div className="pt-4 border-t border-slate-200">
                      <p className="text-sm text-slate-500">
                        <strong>
                          {t('triaxPage.interactionsSection.focusLabel')}{' '}
                        </strong>
                        {interaction.professionals}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Professional Focus Areas */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-4">
              {t('triaxPage.professionalsSection.titleBeforeHighlight')}{' '}
              <span className="gradient-text font-medium">
                {t('triaxPage.professionalsSection.highlight')}
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {professionals.map((prof, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div
                  className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${prof.color} flex items-center justify-center mb-6 shadow-lg`}
                >
                  <prof.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {prof.role}
                </h3>
                <p className="text-purple-600 font-medium text-sm mb-4">
                  {prof.focus}
                </p>
                <p className="text-slate-600 leading-relaxed">
                  {prof.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 p-8 bg-gradient-to-r from-purple-50 to-orange-50 rounded-2xl border border-purple-200">
            <p className="text-center text-slate-700 text-lg">
              {t('triaxPage.professionalsSection.differenceText')}
            </p>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-4">
              {t('triaxPage.benefitsSection.titleBeforeHighlight')}{' '}
              <span className="gradient-text font-medium">
                {t('triaxPage.benefitsSection.highlight')}
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={benefit.id || idx}
                  className="p-6 bg-slate-50 rounded-xl border border-slate-200"
                >
                  <div
                    className={`w-12 h-12 bg-gradient-to-r ${benefit.color} rounded-lg mb-4 flex items-center justify-center`}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-600 text-sm">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-light mb-6">
            {t('triaxPage.cta.titleBeforeHighlight')}{' '}
            <span className="gradient-text font-medium">
              {t('triaxPage.cta.highlight')}
            </span>
            ?
          </h2>
          <p className="text-lg text-slate-300 mb-8 leading-relaxed">
            {t('triaxPage.cta.text')}
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center px-10 py-5 bg-gradient-to-r from-purple-600 to-orange-500 text-white rounded-full font-medium text-lg transition-all duration-300 hover:shadow-glow hover:scale-105"
          >
            <span className="mr-3">{t('triaxPage.cta.button')}</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};
